import React, { useEffect, useRef, useState } from 'react';

declare const __FINDIFY_WEB_CONFIG__: Record<string, string> | undefined;

const config = typeof __FINDIFY_WEB_CONFIG__ === 'undefined' ? {} : __FINDIFY_WEB_CONFIG__;
const API_URL = config.API_URL || 'https://api.findify.cloud';
const DOMAIN_ID = config.DOMAIN_ID || 'workspace2.museudosbiscainhos.findify.cloud';
const POI_ID = config.POI_ID || '770';
const REFRESH_INTERVAL = 60_000;
const LAST_VIDEO_URL_KEY = 'findify-video-kiosk:last-video-url';

type KioskStatus = 'loading' | 'playing' | 'error';

const firstMediaValue = (value: unknown): unknown => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const localized = value as Record<string, unknown>;
  return localized['pt-PT'] ?? localized.pt ?? localized.default ?? Object.values(localized)[0];
};

const mediaUrl = (value: unknown): string | undefined => {
  const media = firstMediaValue(value);
  if (typeof media === 'string') return media.startsWith('http') ? media : `${API_URL}/uploads${media.startsWith('/') ? '' : '/'}${media}`;
  if (!media || typeof media !== 'object') return undefined;
  const object = media as Record<string, unknown>;
  return mediaUrl(object.file_url_path ?? object.file_path ?? object.url ?? object.file_url ?? object.url_path);
};

const getVideoUrl = (poi: Record<string, unknown>) => {
  for (const key of [
    'file_video_media_by_language', 'file_video_media_localized', 'file_video_media',
    'video_media_by_language', 'video_media', 'file_video',
  ]) {
    const url = mediaUrl(poi[key]);
    if (url) return url;
  }
  return undefined;
};

const readPoi = async (): Promise<string> => {
  const response = await fetch(`${API_URL}/front/${DOMAIN_ID}/points_of_interest/${POI_ID}`, {
    headers: { Accept: 'application/json' }, cache: 'no-store',
  });
  if (!response.ok) throw new Error(`A API devolveu ${response.status}.`);
  const payload = await response.json();
  const poi = (payload?.result ?? payload) as Record<string, unknown>;
  const videoUrl = getVideoUrl(poi);
  if (!videoUrl) throw new Error('O POI ativo não tem um ficheiro de vídeo disponível.');
  return videoUrl;
};

export const KioskApp = () => {
  const [status, setStatus] = useState<KioskStatus>('loading');
  const [videoUrl, setVideoUrl] = useState<string | undefined>(() => {
    try { return window.localStorage.getItem(LAST_VIDEO_URL_KEY) ?? undefined; } catch { return undefined; }
  });
  const [message, setMessage] = useState('A carregar vídeo…');
  const currentUrl = useRef<string | undefined>(videoUrl);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const nextUrl = await readPoi();
        if (!mounted) return;
        if (currentUrl.current !== nextUrl) {
          currentUrl.current = nextUrl;
          setVideoUrl(nextUrl);
          try { window.localStorage.setItem(LAST_VIDEO_URL_KEY, nextUrl); } catch { /* Storage is optional. */ }
        }
        setStatus('playing');
      } catch (error) {
        if (!mounted) return;
        if (currentUrl.current || videoUrl) {
          setMessage('Sem ligação. A reproduzir a versão guardada.');
          setStatus('playing');
          return;
        }
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Não foi possível carregar o vídeo.');
      }
    };
    load();
    const refresh = window.setInterval(load, REFRESH_INTERVAL);
    return () => { mounted = false; window.clearInterval(refresh); };
  }, []);

  return <main className="kiosk" aria-live="polite">
    {videoUrl && <video className="kiosk__video" src={videoUrl} autoPlay loop muted playsInline onCanPlay={() => setStatus('playing')} onError={() => { setStatus('error'); setMessage('Não foi possível reproduzir o ficheiro de vídeo.'); }} />}
    {status !== 'playing' && <div className="kiosk__status"><p>{message}</p><small>O ecrã voltará a tentar automaticamente.</small></div>}
  </main>;
};
